import React, { useState } from 'react';

const VonScalperTechnical = () => {
  const [activeTab, setActiveTab] = useState(0);

  const innovations = [
    {
      title: "Multi-Timeframe Convergence",
      description: "Advanced cross-timeframe analysis ensuring signal confluence before execution",
      features: ["15M trend analysis", "5M structure confirmation", "1M precision entry", "Real-time bias detection"],
      icon: "📈"
    },
    {
      title: "Trend Exhaustion Detection",
      description: "Proprietary algorithms identify trend reversal risks before they happen",
      features: ["Momentum decay analysis", "Pullback quality scoring", "Volume confirmation", "Entry quality metrics"],
      icon: "🔄"
    },
    {
      title: "Adaptive Risk Management",
      description: "ML-powered risk controls that adapt to market volatility and performance",
      features: ["Dynamic position sizing", "Performance-based adjustment", "Regime-aware parameters", "Emergency protocols"],
      icon: "🛡️"
    },
    {
      title: "Market Structure Analysis",
      description: "Institutional-grade pattern recognition with ICT methodology integration",
      features: ["Fair Value Gaps", "Order Block detection", "Liquidity zone mapping", "SMT divergence"],
      icon: "🧠"
    }
  ];

  const advantages = [
    {
      title: "Pattern Consensus Technology™",
      description: "Unlike basic pattern indicators, VON SCALPER requires patterns to pass multiple validation criteria including volume confirmation, indicator alignment, and multi-timeframe validation.",
      stats: "Reduces false positives by up to 78%",
      validations: [
        { name: "Multi-Timeframe Validation", desc: "Confirms patterns across multiple timeframes" },
        { name: "Volume Confirmation", desc: "Validates patterns with volume behavior" },
        { name: "Indicator Alignment", desc: "Ensures technical indicators support pattern" },
        { name: "Historical Performance", desc: "Analyzes past success rates of patterns" }
      ]
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white" id="technical">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="h-0.5 w-12 bg-gradient-to-r from-cyan-400 to-purple-400 mr-4"></div>
            <span className="py-2 px-4 rounded-full text-cyan-300 bg-cyan-900/60 font-semibold text-sm border border-cyan-700">TECHNICAL ADVANTAGE</span>
            <div className="h-0.5 w-12 bg-gradient-to-r from-purple-400 to-cyan-400 ml-4"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
            What Sets VON SCALPER Apart
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Key innovations that give you the professional edge in algorithmic scalping with 
            institutional-grade pattern recognition and adaptive intelligence.
          </p>
        </div>

        {/* Key Innovations Tabs */}
        <div className="mb-20">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {innovations.map((innovation, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeTab === index
                    ? 'bg-gradient-to-r from-cyan-600 to-purple-600 text-white shadow-lg'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                }`}
              >
                <span className="mr-2">{innovation.icon}</span>
                {innovation.title}
              </button>
            ))}
          </div>

          <div className="bg-gradient-to-br from-slate-800/50 to-purple-800/50 rounded-2xl p-8 backdrop-blur-sm border border-slate-700">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="text-4xl">{innovations[activeTab].icon}</div>
                  <h3 className="text-3xl font-bold text-white">{innovations[activeTab].title}</h3>
                </div>
                
                <p className="text-xl text-slate-300 leading-relaxed">
                  {innovations[activeTab].description}
                </p>

                <div className="space-y-3">
                  <h4 className="text-lg font-semibold text-cyan-300">Core Components:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {innovations[activeTab].features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-300 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-slate-900 to-purple-900 rounded-xl p-6 border border-slate-600">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 font-medium">System Status</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-green-400 text-sm">ACTIVE</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Pattern Recognition</span>
                        <span className="text-cyan-400 font-semibold">97.3%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Signal Accuracy</span>
                        <span className="text-purple-400 font-semibold">89.1%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Risk Management</span>
                        <span className="text-green-400 font-semibold">OPTIMAL</span>
                      </div>
                    </div>

                    <div className="border-t border-slate-700 pt-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-white mb-1">
                          {activeTab === 0 ? "3.2s" : activeTab === 1 ? "94.7%" : activeTab === 2 ? "1:2.8" : "13 Patterns"}
                        </div>
                        <div className="text-slate-400 text-sm">
                          {activeTab === 0 ? "Avg Analysis Time" : activeTab === 1 ? "Trend Accuracy" : activeTab === 2 ? "Risk:Reward" : "Detected"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pattern Consensus Technology */}
        <div className="bg-gradient-to-r from-slate-800/50 to-purple-800/50 rounded-2xl p-8 md:p-12 backdrop-blur-sm border border-slate-700">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h3 className="text-3xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Pattern Consensus Technology™
                </h3>
                <p className="text-xl text-slate-300 leading-relaxed mb-6">
                  {advantages[0].description}
                </p>
                <div className="inline-flex items-center px-4 py-2 bg-green-900/30 rounded-lg border border-green-700">
                  <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-green-400 font-semibold">{advantages[0].stats}</span>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-cyan-300">Validation Criteria:</h4>
                {advantages[0].validations.map((validation, idx) => (
                  <div key={idx} className="flex items-start space-x-4 p-4 bg-slate-800/30 rounded-lg border border-slate-600">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h5 className="font-semibold text-white mb-1">{validation.name}</h5>
                      <p className="text-slate-400 text-sm">{validation.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              {/* Visual Representation */}
              <div className="bg-gradient-to-br from-slate-900 to-purple-900 rounded-xl p-6 border border-slate-600">
                <h4 className="text-lg font-bold mb-6 text-center text-white">Multi-Layer Validation Process</h4>
                
                <div className="space-y-4">
                  {[
                    { step: "1", label: "Pattern Detection", status: "✓", color: "text-green-400" },
                    { step: "2", label: "Volume Analysis", status: "✓", color: "text-green-400" },
                    { step: "3", label: "Timeframe Sync", status: "✓", color: "text-green-400" },
                    { step: "4", label: "Risk Assessment", status: "⏳", color: "text-yellow-400" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-sm font-bold">
                          {item.step}
                        </div>
                        <span className="text-slate-300">{item.label}</span>
                      </div>
                      <span className={`text-lg ${item.color}`}>{item.status}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-green-900/20 rounded-lg border border-green-700">
                  <div className="text-center">
                    <div className="text-green-400 font-bold">TRADE APPROVED</div>
                    <div className="text-slate-400 text-sm">All validation criteria met</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { title: "Execution Speed", value: "<2.5s", desc: "Average signal processing time" },
            { title: "Pattern Accuracy", value: "94.7%", desc: "Validated pattern success rate" },
            { title: "Risk Control", value: "8.2%", desc: "Maximum historical drawdown" },
            { title: "Market Coverage", value: "24/7", desc: "Continuous market monitoring" }
          ].map((spec, idx) => (
            <div key={idx} className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 text-center border border-slate-700">
              <div className="text-3xl font-bold text-cyan-400 mb-2">{spec.value}</div>
              <div className="text-white font-semibold mb-1">{spec.title}</div>
              <div className="text-slate-400 text-sm">{spec.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VonScalperTechnical;